import {
  Box,
  Divider,
  Grid,
  useTheme,
  Fade,
  Grow,
  Slide,
  Skeleton,
} from '@mui/material';
import OptionChainContent from 'app/components/Simulator/OptionChain';
import { PositionWithExpiryData } from 'app/components/Simulator/OptionChain/types';
// import { calculatePOPForMultiplePositions } from 'app/components/Simulator/PayoffChart/utils';
import TimeLineContent from 'app/components/Simulator/TimeLine';
import CircularLoader from 'app/design/uiComponents/CircularLoader';
import moment from 'moment';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import useGetOptionChainMetadata from 'services/Simulator/useGetOptionChainMetadata';
import useGetOptionChainData, {
  useGetOptionChainExpiredED,
  useGetOptionChainExpiryData,
} from 'services/Simulator/useGetOptionChianData';
import {
  FutureDataType,
  SymbolOptionChainDataType,
} from 'types/ApiServicesTypes';
import OptionPayoff from 'app/components/Simulator/PayoffChart/OptionPayoff/OptionPayoff';
import PositionSquareOffModal from 'app/components/Simulator/OptionChain/PositionSquareOffModal';
import {
  expiredDateSquarOffPosition,
  getExpiredPositions,
} from 'app/components/Simulator/OptionChain/utils';
import PageHeader from 'app/design/speedBot/PageHeader';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './SimulatorAnimations.css';
import { isEqual } from 'lodash';
import MuiChip from 'app/design/uiComponents/MuiChip';

export type OptionTabMetaData = {
  symbol: string;
  optionExpiry: string;
  future: string;
};

export type OptionChainWithExpiryType = Record<
  string,
  {
    option: Record<string, SymbolOptionChainDataType>;
    future: Record<string, FutureDataType>;
  }
>;

const getDefaultDate = () => {
  const today = moment();
  let prev = today.clone().subtract(1, 'days');
  // If previous day is Sunday (0) or Saturday (6), go back to Friday
  if (prev.day() === 0) { // Sunday
    prev = prev.subtract(2, 'days');
  } else if (prev.day() === 6) { // Saturday
    prev = prev.subtract(1, 'days');
  }
  return prev.format('YYYY-MM-DD 09:16:00');
};

// Custom hook for localStorage operations
const useLocalStorage = <T,>(key: string, initialValue: T) => {
  // Check if localStorage is available
  const isLocalStorageAvailable = () => {
    try {
      const test = '__localStorage_test__';
      window.localStorage.setItem(test, test);
      window.localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  };

  // Get from local storage then parse stored json or return initialValue
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (!isLocalStorageAvailable()) {
      return initialValue;
    }
    
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      // Save to local storage only if available
      if (isLocalStorageAvailable()) {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  // Function to clear the localStorage
  const clearStorage = useCallback(() => {
    try {
      if (isLocalStorageAvailable()) {
        window.localStorage.removeItem(key);
      }
      setStoredValue(initialValue);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error clearing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);''

  return [storedValue, setValue, clearStorage] as const;
};

const Simulator = () => {
  const [date, setDate] = useState<string>(getDefaultDate());
  const [open, setOpen] = useState(false);
  const [maxProfitLoss, setMaxProfitLoss] = useState<{
    max_profit: string | number;
    max_loss: string | number;
    breakevenPoints: number[];
  }>({ max_profit: Infinity, max_loss: Infinity, breakevenPoints: [] });
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const { data: queryData, isLoading: isMetadataLoading } =
    useGetOptionChainMetadata(moment.utc(date).format('YYYY-MM-DD').toString());

  const metaData = queryData?.data;
  const indexes = (metaData && Object.keys(metaData.lot_sizes)) || ['NIFTY 50'];
  const [optionTabMetaData, setOptionTabMetaData] = useState<OptionTabMetaData>(
    {
      symbol: indexes[0],
      optionExpiry: (metaData && metaData.options[indexes[0]][0]) || '',
      future: (metaData && metaData.futures[indexes[0]][0]) || '',
    },
  );
  const optionExpiry =
    metaData?.options[optionTabMetaData.symbol || indexes[0]];
  const futureExpiry =
    metaData?.futures[optionTabMetaData.symbol || indexes[0]];
  const [positions, setPositions, clearPositions] = useLocalStorage<PositionWithExpiryData>('simulator-positions', {});
  
  const optionchainWithExpiryData = useGetOptionChainExpiryData(
    optionTabMetaData.symbol,
    [...Object.keys(positions), optionTabMetaData.optionExpiry],
    moment(date, 'YYYYMMDD HH:mm')
      .utcOffset(330)
      .utc()
      .format('YYYY-MM-DD HH:mm:ss')
      .toString(),
  );

  const lotSize = metaData?.lot_sizes[optionTabMetaData.symbol] || 25;

  const { data: optionChainStrikeData, isLoading: isOptionChainLoading } =
    useGetOptionChainData(
      optionTabMetaData.symbol,
      optionTabMetaData.optionExpiry,
      moment(date, 'YYYYMMDD HH:mm')
        .utcOffset(330)
        .utc()
        .format('YYYY-MM-DD HH:mm:ss')
        .toString(),
    );
  const [optionChainDatawithExpiry, setOptionChainDatawithExpiry] =
    useState<OptionChainWithExpiryType>({
      [`${optionTabMetaData.optionExpiry}`]: {
        option: optionChainStrikeData
          ? optionChainStrikeData.candles
          : ({} as Record<string, SymbolOptionChainDataType>),
        future: optionChainStrikeData
          ? optionChainStrikeData.futures
          : ({} as Record<string, FutureDataType>),
      },
    });

  const [optionChianData, setOptionChainData] = useState({
    [optionTabMetaData.optionExpiry]: optionChainStrikeData,
  });

  const expiredEDate: string[] = Object.keys(positions)
    .filter(expiry =>
      !optionExpiry?.includes(expiry) &&
      moment(date).isAfter(moment(expiry), 'day')
    )
    .flatMap(expireE => {
      return Object.values(positions[expireE]).map(position => {
        if (!position.isSquareOff) return expireE;
      });
    })
    .filter((expiry): expiry is string => expiry !== undefined);

  let expireED = expiredEDate.filter(
    (expiry, index) => expiredEDate.indexOf(expiry) === index,
  );
  const optionChainForExpiryDate = useGetOptionChainExpiredED(
    optionTabMetaData.symbol,
    expireED,
  );
  const expPositions =
    optionChainForExpiryDate &&
    expiredDateSquarOffPosition(
      getExpiredPositions(expireED, positions),
      optionChainForExpiryDate,
      lotSize,
    );

  const handleCloseSquareOffModal = (success: boolean) => {
    if (success) {
      let newSquareOffPositions = {} as PositionWithExpiryData;
      expPositions.forEach(position => {
        if (position) {
          newSquareOffPositions = {
            [`${position.expiryDate}`]: {
              ...newSquareOffPositions[`${position.expiryDate}`],
              [`${position.contractType.toUpperCase()}_${position.strike}`]:
                position,
            },
          };
        }
      });
      setPositions({ ...positions, ...newSquareOffPositions });
      expireED = [];
      setOpen(false);
    } else {
      setDate(
        moment(expireED[0])
          .hour(15)
          .minute(30)
          .format('YYYY-MM-DD HH:mm')
          .toString(),
      );
      setOpen(false);
    }
  };

  useEffect(() => {
    setOptionTabMetaData({
      symbol: optionTabMetaData.symbol,
      optionExpiry: optionExpiry ? optionExpiry[0] : '',
      future: futureExpiry ? futureExpiry[0] : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionExpiry]);
  useEffect(() => {
    setOptionChainDatawithExpiry({
      ...optionChainDatawithExpiry,
      [`${optionTabMetaData.optionExpiry}`]: {
        option: optionChainStrikeData
          ? optionChainStrikeData.candles
          : ({} as Record<string, SymbolOptionChainDataType>),
        future: optionChainStrikeData
          ? optionChainStrikeData.futures
          : ({} as Record<string, FutureDataType>),
      },
    });
    setOptionChainData({
      ...optionChianData,
      [optionTabMetaData.optionExpiry]:
        optionChainStrikeData === undefined
          ? Object.values(optionChianData)[
              Object.values(optionChianData).length - 1
            ]
          : optionChainStrikeData,
    });
    if (expireED.length > 0) {
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, optionChainStrikeData]);

  useEffect(() => {
    // Trigger page entrance animation
    const timer = setTimeout(() => {
      setIsPageLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Show loading animation when date changes
    setIsDataLoading(true);
    const timer = setTimeout(() => {
      setIsDataLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [date]);

  const theme = useTheme();

  // Set CSS custom properties for theme colors
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.palette.primary.main);
    root.style.setProperty('--skeleton-color-1', theme.palette.grey[100]);
    root.style.setProperty('--skeleton-color-2', theme.palette.grey[200]);
    root.style.setProperty(
      '--skeleton-shimmer-color',
      `rgba(${theme.palette.primary.main}, 0.1)`,
    );
    root.style.setProperty('--timeline-hover-shadow', theme.shadows[4]);
    root.style.setProperty('--card-hover-shadow', theme.shadows[12]);
  }, [theme]);

  // 1. State to hold the displayed data
  const [displayedOptionChainWithExpiryData, setDisplayedOptionChainWithExpiryData] = useState(optionchainWithExpiryData);
  const prevDataRef = useRef(optionchainWithExpiryData);

  // 2. Effect to update only when new data is available
  useEffect(() => {
    if (
      optionchainWithExpiryData && Object.keys(optionchainWithExpiryData) && Object.keys(optionchainWithExpiryData).length > 0 &&
      !isEqual(optionchainWithExpiryData, prevDataRef.current)
    ) {
      setDisplayedOptionChainWithExpiryData(optionchainWithExpiryData);
      prevDataRef.current = optionchainWithExpiryData;
    }
  }, [optionchainWithExpiryData]);

  return (
    <Fade in={isPageLoaded} timeout={800}>
      <Box>
        <CircularLoader open={false} zIndex={1302} />
        <Helmet>
          <title>Simulator </title>
          <meta name="description" content="Simulator Page" />
        </Helmet>
        <Slide direction="down" in={isPageLoaded} timeout={600}>
          <Box
            className="sticky-top"
            sx={{
              backgroundColor: theme.palette.common.white,
              transition: theme.transitions.create(['transform', 'opacity'], {
                duration: theme.transitions.duration.standard,
              }),
            }}
          >

            {/* <PageHeader variant={''} title={'Simulator'} /> */}
            <PageHeader
          variant="market"
          title={
            <>
              Simulator
              <MuiChip
                  label={'Beta'}
                  size="medium"
                  color={'greylight'}
                  sx={{ m: 1 }}
                />
            </>
          }
        />
            <Divider sx={{ borderBottom: 2, borderColor: 'grey.300' }} />
          </Box>
        </Slide>
        <Box
          className="p-main-spacing"
          sx={{
            backgroundColor: theme.component.mainLayoutContainer.main,
            left: '1px',
            width: `100%`,
            // transition: theme.transitions.create(['opacity', 'transform'], {
            //   duration: theme.transitions.duration.standard,
            // }),
          }}
        >
          <Grid
            container
            columnSpacing={{ xs: 0.5, sm: 1 }}
            rowSpacing={{ xs: 1, sm: 1 }}
          >
            <Grow in={isPageLoaded} timeout={800}>
              <Grid item xs={12} py={{ xs: 0.5, sm: 1 }}>
                <Box className="simulator-timeline fade-in-up">
                  <TimeLineContent date={date} setDate={setDate} positions={positions} />
                </Box>
              </Grid>
            </Grow>
            {(
              <Grid item xs={12} lg={6}>
                <Box
                  className="simulator-card"
                  sx={{
                    height: {
                      xs: '400px',
                      sm: '450px',
                      lg: 'calc(100vh - 200px)',
                    },
                    overflow: 'hidden',
                    borderRadius: '1em',
                  }}
                >
                  <OptionChainContent
                    date={date}
                    optionTabMetaData={optionTabMetaData}
                    setOptionTabMetaData={setOptionTabMetaData}
                    optionChainData={displayedOptionChainWithExpiryData}
                    optionChainCurrentExpiryData={
                      optionChianData[optionTabMetaData.optionExpiry] ||
                      optionChainStrikeData
                    }
                    optionChainMetadata={metaData}
                    lotSize={lotSize}
                    positions={positions}
                    setPositions={setPositions}
                  />
                </Box>
              </Grid>
            )}
            {(
                  <Grid item xs={12} lg={6}>
                    <Box
                      className="simulator-card"
                      sx={{
                        height: {
                          xs: '400px',
                          sm: '450px',
                          lg: 'calc(100vh - 200px)',
                        },
                        border: '1px solid rgb(0 0 0 / 23%)',
                        borderRadius: '1em',
                        mt: { xs: 1, lg: 0 },
                        overflow: 'hidden',
                      }}
                    >
                      <OptionPayoff
                        optionChainDatawithExpiry={displayedOptionChainWithExpiryData}
                        date={date}
                        maxProfitLoss={maxProfitLoss}
                        setMaxProfitLoss={setMaxProfitLoss}
                        position={positions}
                        lotSize={lotSize}
                        spot_price={
                          optionChianData[optionTabMetaData?.optionExpiry]
                            ? optionChianData[optionTabMetaData?.optionExpiry]!
                                .spot_price || optionChainStrikeData?.spot_price
                            : 0
                        }
                        futureData={
                          optionChianData[optionTabMetaData?.optionExpiry]
                            ? optionChianData[optionTabMetaData?.optionExpiry]!
                                .futures || optionChainStrikeData?.futures
                            : {}
                        }
                      />
                    </Box>
                  </Grid>
              )}
          </Grid>
        </Box>
        <PositionSquareOffModal
          open={open}
          handleClose={handleCloseSquareOffModal}
          positions={getExpiredPositions(expireED, positions)}
          currentLTP={optionChainForExpiryDate}
        />
      </Box>
    </Fade>
  );
};

export default Simulator;
